// Import constants
const {
  METERS_SQUARED,
  METERS,
} = require('../constants.js');

// Define component
const AreaCalculator = {
  setup() {
    const { ref, computed, watch, nextTick } = Vue;
    const { useStore } = Vuex;

    const store = useStore();
    const inputMode = ref(store.state.rollWidths ? "dimensions" : "total-area");

    const productType = computed(() => store.state.productType);
    const rollWidths = computed(() => store.state.rollWidths);

    const area = computed({
      get: () => store.state.area,
      set: (value) => store.dispatch("setArea", value)
    });

    const width = computed({
      get: () => {
        if (!store.state.width && store.state.rollWidths) {
          store.dispatch("setWidth", store.state.rollWidths[0]);
          return store.state.rollWidths[0];
        }
        return store.state.width;
      },
      set: (newWidth) => {
        store.dispatch("setWidth", newWidth);

        // updating bulk order display text
        if (store.state.rollWidths && store.state.bulkOrderThreshold) {
          const bulkQtyElements = document.querySelectorAll('.roll-bulk-qty');
          const bulkThreshold = Math.round(newWidth * store.state.bulkOrderThreshold);

          bulkQtyElements.forEach(element => {
            element.innerText = 'Over ' + bulkThreshold + 'm²';
          });
        }
      }
    });

    const length = computed({
      get: () => {
        if (store.state.length === null && store.state.rollWidths) {
          const defaultLength = (1 / store.state.width).toFixed(2);
          store.dispatch("setLength", defaultLength);
          return defaultLength;
        }
        return store.state.length;
      },
      set: (newLength) => store.dispatch("setLength", newLength)
    });

    const toggleInputMode = () => {
      inputMode.value = inputMode.value === "dimensions" ? "total-area" : "dimensions";

      if (area.value != calculateProjectSize()) {
        nextTick(() => {
          width.value = "";
          length.value = "";
        });
      }
    };

    const calculateProjectSize = () => {
      if (area.value && inputMode.value === "total-area") {
        return area.value;
      }

      if (!width.value || !length.value) {
        return null;
      }

      return (width.value * length.value).toFixed(2);
    };

    watch([width, length], () => {
      if (width.value !== null && length.value !== null) {
        area.value = calculateProjectSize();
      }
    }, { immediate: true });

    return {
      inputMode,
      productType,
      rollWidths,
      area,
      width,
      length,
      toggleInputMode,
      store,
      METERS_SQUARED,
      METERS,
    };
  },

  template: `
  <div class="area-calculator-wrapper">
    <div v-if="productType == 'pack'" class="w-full calc_meaurement-type-block">
      <div class="whitespace-nowrap input-label">Measurement type</div>
      <div class="toggle-wrapper type-toggle-main">
        <div class="toggle-container" @click="toggleInputMode">
          <div id="areaCheck" :class="{ checked: inputMode === 'total-area' }">
            Area
          </div>
          <div id="dimensionCheck" :class="{ checked: inputMode === 'dimensions' }">
            W x L
          </div>
          <div class="slider"></div>
        </div>
      </div>
    </div>
    <div v-else class="w-full">
      <div class="input-label">Width</div>
      <div class="w-full select-field-wrapper">
        <select class="w-full" v-model="width">
          <option v-for="(widthValue, index) in rollWidths" :key="index">
            {{ Number(widthValue) }}
          </option>
        </select>
      </div>
    </div>

    <div class="flex flex-row gap-5 items-center w-full">
      <div v-if="inputMode === 'dimensions'" class="w-auto calc_dimension-wrappers">
        <div class="flex space-x-2">
          <div class="w-full input-wrapper" v-if="productType == 'pack'">
            <div class="input-label">Width</div>
            <div class="relative">
              <input type="number" min="0" max="1000" v-model.number="width" placeholder="E.g. 2"
                class="w-full input-field noscroll" />
              <span class="input-suffix">{{ METERS }}</span>
            </div>
          </div>
          <div class="w-full input-wrapper">
            <div class="input-label">Length</div>
            <div class="relative">
              <input type="number" min="0" max="1000" v-model.number="length" placeholder="E.g. 2"
                class="w-full input-field noscroll" />
              <span class="input-suffix">{{ METERS }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="w-full calc_dimension-wrappers">
        <div class="w-full input-wrapper">
          <div class="input-label">Area</div>
          <div class="relative">
            <input type="number" min="0" max="1000" v-model.number="area" placeholder="E.g. 2"
              class="w-full input-field noscroll" />
            <span class="input-suffix">{{ METERS_SQUARED }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
    `
};

// Export the component using module.exports
module.exports = { AreaCalculator }; 